import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star, CreditCard, RefreshCw, ChevronRight, ArrowRight, Check } from "lucide-react";
import { trackStep } from "../utils/funnel";

const RED = "#E82529";
import axios from "axios";
import { API } from "../utils/constants";

// mode: "generate" (default — gate before building a trip) | "unlock" (a
// teaser trip is built and waiting; paying unfolds it).
const PaymentModal = ({ isOpen, onClose, onCreditsReady, userEmail, setUserEmail, mode = "generate" }) => {
  useEffect(() => { if (isOpen) trackStep("payment_open"); }, [isOpen]);
  const [email, setEmail] = useState(userEmail || "");
  const [step, setStep] = useState("email"); // email | packages
  const [packages, setPackages] = useState([]);
  const [plansV2, setPlansV2] = useState(false);          // the two plans of docs/producto 2.4 (server switch PLANS_V2)
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // All plans are always on the table — the user chooses.
  const [showAllPlans, setShowAllPlans] = useState(true);

  useEffect(() => {
    if (isOpen && userEmail) {
      setEmail(userEmail);
      checkCredits(userEmail).catch(() => setStep("email"));   // offline: they can try again from the email step
    } else if (isOpen) {
      setStep("email");
      setShowAllPlans(true);
    }
  }, [isOpen, userEmail]);

  const checkCredits = async (e) => {
    const res = await axios.get(`${API}/credits/check?email=${encodeURIComponent(e)}`);
    if (res.data.credits > 0) {
      onCreditsReady(e, res.data.credits);
      return;
    }
    // No free generations — a new email goes straight to the plans. Their
    // preview (Day 1 of a real trip) is what sells; this is where they choose.
    const pkgRes = await axios.get(`${API}/packages`);
    setPackages(pkgRes.data.packages || []);
    setPlansV2(!!pkgRes.data.plans_v2);
    setStep("packages");
  };

  const handleEmailSubmit = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) { setError("Enter a valid email"); return; }
    setError("");
    setIsLoading(true);
    try {
      localStorage.setItem("ju_email", trimmed);
      setUserEmail(trimmed);
      await checkCredits(trimmed);
    } catch (e) {
      setError("Something went wrong. Try again.");
    }
    setIsLoading(false);
  };

  const handleBuy = async (packageId) => {
    setIsLoading(true);
    setError("");
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/payment/create-checkout`, {
        package_id: packageId,
        email: email.trim().toLowerCase(),
        origin_url: originUrl,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) {
      setError("Payment setup failed. Try again.");
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const discover = packages.find(p => p.id === "discover");
  const journey = packages.find(p => p.id === "journey");
  const unfolded = packages.find(p => p.id === "unfolded");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full overflow-y-auto"
        style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 8, maxWidth: showAllPlans ? "640px" : "420px", maxHeight: "90vh" }}
        data-testid="payment-modal"
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--zen-border)" }}>
          <div className="flex items-center gap-2.5">
            <Star className="w-4 h-4" style={{ color: RED, fill: RED }} />
            <h3 style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 800, fontSize: 18, color: "var(--zen-text)" }}>
              {step === "email" ? "Almost there" : plansV2 ? "Plan your trip" : "Choose your pass"}
            </h3>
          </div>
          <button onClick={onClose} className="inline-flex items-center justify-center hover:opacity-70 transition-opacity" style={{ width: 28, height: 28, borderRadius: "50%", border: "1px solid var(--zen-border-h)", color: "var(--zen-dim)", background: "transparent" }} data-testid="payment-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5">
          <AnimatePresence mode="wait">
            {/* Step 1: Email */}
            {step === "email" && (
              <motion.div key="email" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <p className="text-sm mb-4" style={{ color: "var(--zen-sub)" }}>
                  {mode === "unlock"
                    ? "Enter your email to unfold your full itinerary."
                    : "Enter your email to plan your trip."}
                </p>
                <input
                  type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleEmailSubmit()}
                  placeholder="your@email.com"
                  className="jf-field w-full mb-3"
                  data-testid="payment-email-input"
                  autoFocus
                />
                {error && <p className="text-xs mb-3" style={{ color: "var(--cat-nightlife-solid)" }}>{error}</p>}
                <button onClick={handleEmailSubmit} disabled={isLoading} className="zen-btn zen-btn-primary w-full py-3" data-testid="payment-email-submit">
                  {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><ArrowRight className="w-4 h-4" /> Continue</>}
                </button>
              </motion.div>
            )}

            {/* Step 2: Packages */}
            {step === "packages" && !plansV2 && !showAllPlans && discover && (
              <motion.div key="packages-single" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <p className="text-sm mb-4" style={{ color: "var(--zen-sub)" }}>
                  Choose a plan to keep planning.
                </p>

                {/* Primary: Discover */}
                <TierCard pkg={discover} onBuy={() => handleBuy("discover")} isLoading={isLoading} primary />

                <button
                  onClick={() => setShowAllPlans(true)}
                  className="w-full mt-3 py-2.5 text-sm font-medium rounded-xl transition-all hover:opacity-80 flex items-center justify-center gap-1.5"
                  style={{ color: "var(--zen-accent)", border: "1px solid var(--zen-border)" }}
                  data-testid="see-all-plans-btn"
                >
                  See all plans <ChevronRight className="w-3.5 h-3.5" />
                </button>

                {error && <p className="text-xs mt-3" style={{ color: "var(--cat-nightlife-solid)" }}>{error}</p>}
                {isLoading && <LoadingRedirect />}
              </motion.div>
            )}

            {/* the two plans (docs/producto 2.4): plan the trip, or plan it and take us with you — words provisional */}
            {step === "packages" && plansV2 && (
              <motion.div key="packages-v2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <p className="text-sm mb-4" style={{ color: "var(--zen-sub)" }}>One purchase for your trip, good for 12 months. No subscription.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {packages.filter((p) => !p.upgrade).map((p) => (
                    <TierCard key={p.id} pkg={p} onBuy={() => handleBuy(p.id)} isLoading={isLoading} featured={p.has_companion} />
                  ))}
                </div>
                {error && <p className="text-xs mt-3" style={{ color: "var(--cat-nightlife-solid)" }}>{error}</p>}
                {isLoading && <LoadingRedirect />}
              </motion.div>
            )}

            {/* Step 2b: All 3 plans */}
            {step === "packages" && !plansV2 && showAllPlans && (
              <motion.div key="packages-all" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <p className="text-sm mb-4" style={{ color: "var(--zen-sub)" }}>
                  {mode === "unlock"
                    ? "Your trip is built and waiting — pick a plan to unfold every day of it. One-time purchase, no subscription."
                    : "One-time purchase. No subscription. Yours forever."}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {discover && <TierCard pkg={discover} onBuy={() => handleBuy("discover")} isLoading={isLoading} />}
                  {journey && <TierCard pkg={journey} onBuy={() => handleBuy("journey")} isLoading={isLoading} featured />}
                  {unfolded && <TierCard pkg={unfolded} onBuy={() => handleBuy("unfolded")} isLoading={isLoading} />}
                </div>

                {error && <p className="text-xs mt-3" style={{ color: "var(--cat-nightlife-solid)" }}>{error}</p>}
                {isLoading && <LoadingRedirect />}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

const TierCard = ({ pkg, onBuy, isLoading, primary, featured }) => {
  const features = {
    discover: ["1 trip planning", "3 AI generations", "PDF export"],
    journey: ["3 trip plannings", "12 AI generations", "PDF export"],
    unfolded: ["Unlimited planning", "40 AI generations", "PDF export", "Mobile companion", "On-the-go recalculate"],
    // the two plans (provisional words until marketing; docs/producto 2.4)
    planner: ["Your whole trip, planned with you", "Your days as a calendar, to shape by hand", "5 full itineraries and the PDF", "12 months to plan it"],
    planner_companion: ["Everything in Trip Planner", "The Companion in Japan: your day changed when something changes", "Food near you, now, and the evening briefing"],
    companion: ["Your day changed when rain or tiredness changes it", "Food near you, now", "The evening briefing"],
  };
  const tierFeatures = features[pkg.id] || [];

  return (
    <button
      onClick={onBuy}
      disabled={isLoading}
      className={`w-full rounded-xl text-left transition-all hover:shadow-md relative overflow-hidden ${primary ? "p-5" : "p-4"}`}
      style={{
        background: "var(--zen-s1)",
        border: featured ? "2px solid var(--zen-accent)" : "1.5px solid var(--zen-border)",
      }}
      data-testid={`buy-package-${pkg.id}`}
    >
      {pkg.badge && (
        <div className="absolute top-0 right-0 text-[8px] px-2.5 py-1 rounded-bl-lg font-bold uppercase tracking-wider"
          style={{ background: pkg.id === "journey" ? "var(--zen-accent)" : "var(--cat-culture-solid)", color: "#fff" }}>
          {pkg.badge}
        </div>
      )}

      <div className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--zen-accent)" }}>
        {{ discover: "Discover", journey: "Journey", unfolded: "Unfolded" }[pkg.id] || pkg.name}
      </div>

      <div className="flex items-baseline gap-1.5 mb-1">
        <span className={`font-bold ${primary ? "text-3xl" : "text-2xl"}`} style={{ color: "var(--zen-text)" }}>${pkg.amount} <span className="text-sm font-normal" style={{ color: "var(--zen-dim)" }}>USD</span></span>
        {/* never a crossed-out price that was not real (docs/producto 2.4) */}
        {pkg.regular_price > pkg.amount && <span className="text-xs line-through" style={{ color: "var(--zen-dim)" }}>${pkg.regular_price}</span>}
      </div>

      <p className="text-[10px] mb-2" style={{ color: "var(--zen-dim)" }}>
        one-time · {pkg.trips_label}
      </p>

      <div className="space-y-1 mb-3">
        {tierFeatures.map((f, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <Check className="w-3 h-3 shrink-0" style={{ color: "var(--zen-accent)" }} />
            <span className="text-[11px]" style={{ color: "var(--zen-sub)" }}>{f}</span>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-1 text-[10px] font-medium pt-2" style={{ color: "var(--zen-accent)", borderTop: "1px solid var(--zen-border)" }}>
        <CreditCard className="w-3 h-3" /> {primary ? "Get started" : "Choose"} <ChevronRight className="w-3 h-3 ml-auto" />
      </div>
    </button>
  );
};

const LoadingRedirect = () => (
  <div className="flex items-center justify-center py-3 gap-2">
    <RefreshCw className="w-4 h-4 animate-spin" style={{ color: "var(--zen-accent)" }} />
    <span className="text-xs" style={{ color: "var(--zen-sub)" }}>Redirecting to checkout...</span>
  </div>
);

export default PaymentModal;
